import {
  DocumentOrientationEnum,
  RequiredDocumentTypeEnum
} from "@repo/core/enums/proposals";
import { PROPOSTA_SITUACAO } from "@repo/core/services/entities/Proposals";
import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { persistPreProposalAlterarDataConferencePending } from "../../services/persistPreProposalAlterarPending.service";
import {
  cancelPreProposalAlterarSyncDebounce,
  schedulePreProposalAlterarSync
} from "../../services/preProposalAlterarSyncDebounce.service";
import { mergePreProposalPersistedState } from "@repo/core/utils/layoutBandeiras.utils";

export type QueueProposalEditMode = "proposal" | "preProposal";

export interface PreProposalData {
  strIdProcessoUnico?: string;
  lngProposta?: number;
  intScore?: number;
  strProduto?: string;
  strPontoAtendimento?: string;
  strBandeira?: string;
  strNome?: string;
  strNroDocIdent?: string;
  strUfEmiDocIdent?: string;
  strTipoDocIdent?: string;
  datNascimento?: string;
  strDataEmiDocIdent?: string;
  strEstadoCivil?: string;
  strOrgaoEmissor?: string;
  strSexo?: string;
  strNomeMae?: string;
  strNomePai?: string;
  strCliente?: string;
  strNomeDocumento?: string;
  strNomeArquivoBiometria?: string;
  strArquivoBiometria?: string;
  strBase64Biometria?: string;
  strEndDdd1?: string;
  strEndFone1?: string;
  strEndTipoFone1?: string;
  strAtendente?: string;
  strSituacao?: PROPOSTA_SITUACAO;
  intPacoteBeneficio?: number;
  blnExisteRegistroUnico?: boolean;
  uploadedProposalDocumentTypes?: string[];
  unicoStatus?: "available" | "unavailable";
  shouldForceManualCapture?: boolean;
  restartRequired?: boolean;
  shouldForceNewBiometry?: boolean;
  biometryReuseProcessId?: string;
  strEmpresa?: string;
  needsContextUpdate?: boolean;
  /** Identity doc types (0001, 0010, 0026, etc.) already sent via Unico SDK — hidden on manual document list */
  unicoUploadedIdentityDocumentTypes?: string[];
  /**
   * Document type codes excluded from manual capture UI (e.g. 0017 after automatic upload failed on Unico reuse path).
   */
  manualCaptureExcludedDocumentTypes?: string[];
  // Client contact and address fields (synced on reanalysis / Proposta/Alterar)
  strEmail?: string;
  strInfoEmail?: string;
  strResCep?: string;
  strResEndereco?: string;
  strResNumero?: string;
  strResBairro?: string;
  strResCidade?: string;
  strResUF?: string;
  strResComplemento?: string;
  strResTipoFone1?: string;
  strEndDdd2?: string;
  strEndFone2?: string;
  strEndTipoFone2?: string;
  strResDDD2?: string;
  strResFone2?: string;
  strResTipoFone2?: string;
  // Employment fields
  strAtivEmpresa?: string;
  strAtivCargo?: string;
  strAtivSetor?: string;
  strProfissao?: string;
  strAtivProfissao?: string;
  strAtivDataAdmissao?: string;
  datAtivDataAdmissao?: string;
  datTrabDesde?: string;
  strTrabalhaDesde?: string;
  strRenda?: number;
  dblRenda?: number;
  dblComSalario?: number;
  strClasseProfissional?: string;
  // Residence and reference fields
  strEndTipoResidencia?: string;
  strEndMesAno?: string;
  strRefPessNome1?: string;
  strRefPessRel1?: string;
  strRefPessFone1?: string;
  strRefPessDdd1?: string;
  strCorrespondencia?: string;
  // Proposal classification fields
  strTipoPessoa?: string;
  strTipoProposta?: string;
  strOpcaoPagamentos?: string;
  strTipoCorrespondencia?: string;
  strReavaliacaoLimite?: string;
  // Card billing fields (kept in sync with Proposta/Incluir on reanalysis)
  intDiaCartao?: number;
  strEnvioFatura?: string;
  /** Local-only mode used when editing an existing item from the analysis queue. */
  queueEditMode?: QueueProposalEditMode;
}

export interface RequiredDocuments {
  strTipoDocumento: RequiredDocumentTypeEnum;
  strDescricao: string;
  strNumeroPaginas: number;
  strOrientacaoPagina: DocumentOrientationEnum;
  strDocBase64?: string;
  isUploaded?: boolean;
  uploadedAt?: string;
}

export type SetProposalDataOptions = {
  /** When true, skips debounced POST /Proposta/Alterar (e.g. caller already persisted). */
  skipAlterarSync?: boolean;
};

interface Actions {
  setProposalData: (
    proposalData: Partial<PreProposalData>,
    options?: SetProposalDataOptions
  ) => void;
  setRequiredDocuments: (requiredDocuments: RequiredDocuments[]) => void;
  updateRequiredDocument: (
    id: string,
    requiredDocument: RequiredDocuments
  ) => void;
  markDocumentAsUploaded: (documentType: string) => void;
  areAllDocumentsUploaded: () => boolean;
  getPendingDocuments: () => RequiredDocuments[];
  hasBiometryDocuments: () => boolean;
  resetPreProposalStore: () => void;
}

export interface PreProposalState {
  proposalData?: PreProposalData;
  requiredDocuments?: RequiredDocuments[];
}

const initialValues: PreProposalState = {
  proposalData: {
    strIdProcessoUnico: undefined,
    intScore: undefined,
    lngProposta: undefined,
    strProduto: "",
    strPontoAtendimento: undefined,
    strNome: undefined,
    strBandeira: undefined,
    strCliente: undefined,
    datNascimento: undefined,
    strNomeMae: undefined,
    strNomePai: undefined,
    strNroDocIdent: undefined,
    strOrgaoEmissor: undefined,
    strUfEmiDocIdent: undefined,
    strDataEmiDocIdent: undefined,
    strTipoDocIdent: undefined,
    strAtendente: undefined,
    strSituacao: undefined,
    strEndDdd1: undefined,
    strEndFone1: undefined,
    strEndTipoFone1: undefined,
    intPacoteBeneficio: undefined,
    uploadedProposalDocumentTypes: [],
    unicoStatus: undefined,
    shouldForceManualCapture: undefined,
    restartRequired: undefined,
    shouldForceNewBiometry: undefined,
    biometryReuseProcessId: undefined,
    strEmpresa: undefined,
    needsContextUpdate: undefined,
    unicoUploadedIdentityDocumentTypes: undefined,
    manualCaptureExcludedDocumentTypes: undefined,
    strEmail: undefined,
    strInfoEmail: undefined,
    strResCep: undefined,
    strResEndereco: undefined,
    strResNumero: undefined,
    strResBairro: undefined,
    strResCidade: undefined,
    strResUF: undefined,
    strResComplemento: undefined,
    strResTipoFone1: undefined,
    strEndDdd2: undefined,
    strEndFone2: undefined,
    strEndTipoFone2: undefined,
    strResDDD2: undefined,
    strResFone2: undefined,
    strResTipoFone2: undefined,
    strAtivEmpresa: undefined,
    strAtivCargo: undefined,
    strAtivSetor: undefined,
    strProfissao: undefined,
    strAtivProfissao: undefined,
    strAtivDataAdmissao: undefined,
    datAtivDataAdmissao: undefined,
    datTrabDesde: undefined,
    strTrabalhaDesde: undefined,
    strRenda: undefined,
    dblRenda: undefined,
    dblComSalario: undefined,
    strClasseProfissional: undefined,
    strEndTipoResidencia: undefined,
    strEndMesAno: undefined,
    strRefPessNome1: undefined,
    strRefPessRel1: undefined,
    strRefPessFone1: undefined,
    strRefPessDdd1: undefined,
    strCorrespondencia: undefined,
    strTipoPessoa: undefined,
    strTipoProposta: undefined,
    strOpcaoPagamentos: undefined,
    strTipoCorrespondencia: undefined,
    strReavaliacaoLimite: undefined,
    intDiaCartao: undefined,
    strEnvioFatura: undefined,
    queueEditMode: undefined
  },
  requiredDocuments: []
};

export const usePreProposalStore = create<PreProposalState & Actions>()(
  persist(
    (set, get) => ({
      ...initialValues,
      setProposalData: (
        proposalData: Partial<PreProposalData>,
        options?: SetProposalDataOptions
      ) => {
        const data = get();
        const currentName = data?.proposalData?.strNome?.trim();
        const incomingName = proposalData.strNome?.trim();
        const merged: PreProposalData = {
          ...data?.proposalData,
          ...proposalData,
          strNome:
            proposalData.strNome !== undefined
              ? incomingName || currentName
              : data?.proposalData?.strNome
        };
        set({
          ...data,
          proposalData: merged
        });
        if (!options?.skipAlterarSync && merged.lngProposta) {
          schedulePreProposalAlterarSync(() => {
            const latest = get().proposalData;
            if (latest?.lngProposta) {
              void persistPreProposalAlterarDataConferencePending(latest).catch(
                () => undefined
              );
            }
          });
        }
      },
      setRequiredDocuments: (requiredDocuments: RequiredDocuments[]) => {
        const data = get();
        set({
          ...data,
          requiredDocuments: requiredDocuments
        });
      },
      updateRequiredDocument: (
        id: string,
        requiredDocument: RequiredDocuments
      ) => {
        const data = get();
        set({
          ...data,
          requiredDocuments: data?.requiredDocuments?.map((document) =>
            document.strTipoDocumento === id ? requiredDocument : document
          )
        });
      },
      markDocumentAsUploaded: (documentType: string) => {
        const data = get();
        const normalizedDocumentType = String(documentType).trim();
        const updatedDocuments = data?.requiredDocuments?.map((document) => {
          const normalizedDocType = String(document.strTipoDocumento).trim();
          if (normalizedDocType === normalizedDocumentType) {
            return {
              ...document,
              isUploaded: true,
              uploadedAt: new Date().toISOString()
            };
          }
          return document;
        });
        set({
          ...data,
          requiredDocuments: updatedDocuments
        });
      },
      areAllDocumentsUploaded: () => {
        const data = get();
        const requiredDocuments = data?.requiredDocuments || [];
        if (requiredDocuments.length === 0) {
          return false;
        }
        const excluded = new Set(
          (data?.proposalData?.manualCaptureExcludedDocumentTypes ?? []).map(
            (t) => String(t).trim()
          )
        );
        return requiredDocuments.every((document) => {
          const docType = String(document.strTipoDocumento).trim();
          if (excluded.has(docType)) {
            return true;
          }
          return document.isUploaded === true;
        });
      },
      getPendingDocuments: () => {
        const data = get();
        const requiredDocuments = data?.requiredDocuments || [];
        const excluded = new Set(
          (data?.proposalData?.manualCaptureExcludedDocumentTypes ?? []).map(
            (t) => String(t).trim()
          )
        );
        return requiredDocuments.filter((document) => {
          const docType = String(document.strTipoDocumento).trim();
          if (excluded.has(docType)) {
            return false;
          }
          return document.isUploaded !== true;
        });
      },
      hasBiometryDocuments: () => {
        const data = get();
        const requiredDocuments = data?.requiredDocuments || [];
        return requiredDocuments.some(
          (doc) =>
            String(doc.strTipoDocumento).trim() ===
            RequiredDocumentTypeEnum.BIOMETRY
        );
      },
      resetPreProposalStore: () => {
        cancelPreProposalAlterarSyncDebounce();
        set(initialValues);
      }
    }),
    {
      name: "PreProposalStore",
      storage: createJSONStorage(() => AsyncStorage),
      merge: (persistedState, currentState) =>
        mergePreProposalPersistedState(persistedState, currentState)
    }
  )
);
